import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function SummaryCard({
    title,
    subtitle,
    items,
    showPrintIcon = true,
    shadow = true,
    customIcon = 'print',
    truncateValues = false,
    subTitleColor = 'text',
}) {
    const renderItems = useMemo(() => {
        const labels = Object.keys(items);
        const values = Object.values(items);

        return labels.map((label, key) => (
            <MDBox key={label} display="flex" py={1} pr={2} width="100%" overflow="hidden">
                <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                    sx={{
                        flexShrink: 0,
                    }}
                >
                    {label}: &nbsp;
                </MDTypography>
                <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={[{
                        flexGrow: 1
                    }, truncateValues && {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }]}
                >
                    &nbsp;{values[key]}
                </MDTypography>
            </MDBox>
        ));
    }, [items, truncateValues]);

    return (
        <Card sx={{ height: '100%', boxShadow: !shadow && 'none' }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
                <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                    {title}
                </MDTypography>
                {showPrintIcon && (
                    <MDTypography variant="body2" color="secondary">
                        <Icon>{customIcon}</Icon>
                    </MDTypography>
                )}
            </MDBox>
            <MDBox p={2}>
                {subtitle && (
                    <MDBox mb={1} lineHeight={1}>
                        <MDTypography variant="button" color={subTitleColor} fontWeight="light">
                            {subtitle}
                        </MDTypography>
                    </MDBox>
                )}
                <MDBox>
                    <Divider />
                </MDBox>
                <MDBox>{renderItems}</MDBox>
            </MDBox>
        </Card>
    );
}

SummaryCard.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    items: PropTypes.object.isRequired,
    showPrintIcon: PropTypes.bool,
    shadow: PropTypes.bool,
    customIcon: PropTypes.string,
    truncateValues: PropTypes.bool,
    subTitleColor: PropTypes.string,
};

SummaryCard.defaultProps = {
    subtitle: '',
    showPrintIcon: true,
    shadow: true,
    customIcon: 'print',
    truncateValues: false,
    subTitleColor: 'text',
};

export default React.memo(SummaryCard);
